/**
 * Rich content card builders for AI Career Advisor tool results.
 * Cards are displayed inline in the chat as styled Tailwind components.
 */

/**
 * Render a card for a tool result based on the tool name.
 * @param {string} toolName - The tool that produced the result
 * @param {Object} result - The tool result data
 * @returns {HTMLElement|null} A card element, or null if unsupported
 */
export function renderToolCard(toolName, result) {
  if (result.error) return renderErrorCard(result.error)

  switch (toolName) {
    case "salary_benchmark":
      return renderBenchmarkCard(result)
    case "calculate_take_home":
      return renderTakeHomeCard(result)
    case "compare_scenarios":
      return renderComparisonCard(result)
    case "suggest_calculators":
      return renderCalculatorLinksCard(result)
    case "skills_gap_analysis":
      return renderSkillsGapCard(result)
    case "market_research":
      return renderMarketResearchCard(result)
    case "suggest_cv_builder":
      return renderCvBuilderCard(result)
    default:
      return null
  }
}

/**
 * Salary benchmark card with percentile and comparison info.
 */
export function renderBenchmarkCard(data) {
  if (!data.has_data) {
    return null
  }

  const percentile = data.percentile_rank != null ? Math.round(data.percentile_rank) : null
  const gaugeHtml = percentile != null ? createPercentileGauge(percentile) : ""

  const diffSign = data.difference_from_median > 0 ? "+" : ""
  const diffColor = data.difference_from_median >= 0 ? "text-green-600" : "text-red-600"

  const bodyHtml = `
    <div class="space-y-3">
      <div class="flex items-center justify-between text-sm">
        <span class="text-gray-600">Role</span>
        <span class="font-medium">${escapeHtml(data.role_name || "")}${data.level_name ? ` (${escapeHtml(data.level_name)})` : ""}</span>
      </div>
      ${data.location_name ? `<div class="flex items-center justify-between text-sm"><span class="text-gray-600">Location</span><span class="font-medium">${escapeHtml(data.location_name)}</span></div>` : ""}
      ${gaugeHtml}
      <div class="grid grid-cols-2 gap-3">
        <div class="bg-gray-50 rounded-lg p-3 text-center">
          <div class="text-xs text-gray-500">Your salary</div>
          <div class="text-lg font-semibold">${formatCurrency(data.salary)}</div>
        </div>
        <div class="bg-gray-50 rounded-lg p-3 text-center">
          <div class="text-xs text-gray-500">Median</div>
          <div class="text-lg font-semibold">${formatCurrency(data.median)}</div>
        </div>
      </div>
      ${data.difference_from_median != null ? `<div class="text-center text-sm"><span class="${diffColor} font-medium">${diffSign}${formatCurrency(data.difference_from_median)}</span> <span class="text-gray-500">${data.comparison_text || "vs median"}</span></div>` : ""}
      ${data.next_level ? renderNextLevel(data.next_level) : ""}
    </div>
  `
  return createCard("Salary Benchmark", bodyHtml)
}

/**
 * Take-home pay card with deductions breakdown.
 */
export function renderTakeHomeCard(data) {
  const deductions = [
    { label: "PAYE Tax", value: data.paye },
    { label: "ACC Levy", value: data.acc_levy },
    { label: "KiwiSaver", value: data.kiwisaver_employee }
  ]
  if (data.student_loan > 0) {
    deductions.push({ label: "Student Loan", value: data.student_loan })
  }

  const netPct = data.gross_salary > 0 ? Math.round((data.net_salary / data.gross_salary) * 100) : 0

  const bodyHtml = `
    <div class="space-y-3">
      <div class="flex items-center justify-between">
        <div>
          <div class="text-xs text-gray-500">Gross Salary</div>
          <div class="text-lg font-semibold">${formatCurrency(data.gross_salary)}</div>
        </div>
        <div class="text-right">
          <div class="text-xs text-gray-500">Take-Home</div>
          <div class="text-lg font-semibold text-green-600">${formatCurrency(data.net_salary)}</div>
        </div>
      </div>
      <div class="relative h-3 bg-gray-200 rounded-full overflow-hidden">
        <div class="absolute inset-y-0 left-0 bg-green-500 rounded-full" style="width: ${netPct}%"></div>
      </div>
      <div class="text-center text-xs text-gray-500">You keep ${netPct}% of your gross salary</div>
      <div class="divide-y divide-gray-100">
        ${deductions.map(d => `<div class="flex items-center justify-between py-1.5 text-sm"><span class="text-gray-600">${d.label}</span><span class="font-medium text-red-600">-${formatCurrency(d.value)}</span></div>`).join("")}
      </div>
      ${data.effective_tax_rate != null ? `<div class="text-center text-xs text-gray-500">Effective tax rate: ${data.effective_tax_rate}%</div>` : ""}
    </div>
  `
  return createCard("Take-Home Pay", bodyHtml)
}

/**
 * Comparison card showing two scenarios side-by-side.
 */
export function renderComparisonCard(data) {
  const a = data.scenario_a || {}
  const b = data.scenario_b || {}
  const diff = data.differences || {}

  const bodyHtml = `
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-gray-200">
            <th class="py-2 text-left text-gray-500 font-normal"></th>
            <th class="py-2 text-right text-gray-700 font-medium">Scenario A</th>
            <th class="py-2 text-right text-gray-700 font-medium">Scenario B</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          ${comparisonRow("Gross", a.gross_salary, b.gross_salary)}
          ${comparisonRow("Net", a.net_salary, b.net_salary)}
          ${comparisonRow("PAYE", a.total_tax, b.total_tax)}
          ${comparisonRow("KiwiSaver", a.kiwisaver_employee, b.kiwisaver_employee)}
        </tbody>
      </table>
    </div>
    ${diff.net_salary != null ? `<div class="mt-3 text-center text-sm"><span class="${diff.net_salary >= 0 ? "text-green-600" : "text-red-600"} font-medium">${diff.net_salary >= 0 ? "+" : ""}${formatCurrency(diff.net_salary)}</span> <span class="text-gray-500">net difference</span></div>` : ""}
  `
  return createCard("Scenario Comparison", bodyHtml)
}

/**
 * Calculator suggestions card with links.
 */
export function renderCalculatorLinksCard(data) {
  const calcs = data.calculators || []
  if (calcs.length === 0) return null

  const bodyHtml = `
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
      ${calcs.map(c => `
        <a href="${escapeHtml(c.path)}" target="_blank" rel="noopener"
           class="flex items-center gap-2 p-3 rounded-lg border border-gray-200 hover:border-cyan-300 hover:bg-cyan-50 transition-colors no-underline">
          <svg class="w-4 h-4 text-cyan-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18zm2.498-6.75h.008v.008H15.75v-.008zm0 2.25h.008v.008H15.75V13.5z" />
          </svg>
          <div>
            <div class="text-sm font-medium text-gray-900">${escapeHtml(c.name)}</div>
            <div class="text-xs text-gray-500">${escapeHtml(c.desc)}</div>
          </div>
        </a>
      `).join("")}
    </div>
  `
  return createCard("Related Calculators", bodyHtml)
}

/**
 * Skills gap analysis card showing overlap and gap skills.
 */
export function renderSkillsGapCard(data) {
  const overlapList = (data.overlap_skills || []).map(s =>
    `<li class="flex items-center gap-1.5 text-sm"><span class="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0"></span>${escapeHtml(s)}</li>`
  ).join("")

  const gapList = (data.gap_skills || []).map(s =>
    `<li class="flex items-center gap-1.5 text-sm"><span class="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"></span>${escapeHtml(s)}</li>`
  ).join("")

  const demandBadge = data.demand_level
    ? `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${demandBadgeColor(data.demand_level)}">${escapeHtml(data.demand_level)}</span>`
    : ""

  const dbNotice = !data.has_db_data
    ? `<div class="mt-2 text-xs text-gray-400 italic">Based on general market knowledge</div>`
    : ""

  const bodyHtml = `
    <div class="space-y-3">
      <div class="flex items-center justify-between text-sm">
        <span class="text-gray-600">${escapeHtml(data.current_role_name || "")}</span>
        <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
        </svg>
        <span class="font-medium">${escapeHtml(data.target_role_name || "")}</span>
      </div>
      ${demandBadge ? `<div class="text-center">${demandBadge}</div>` : ""}
      ${overlapList ? `<div><div class="text-xs font-medium text-green-700 mb-1">Skills you already have</div><ul class="space-y-1">${overlapList}</ul></div>` : ""}
      ${gapList ? `<div><div class="text-xs font-medium text-red-700 mb-1">Skills to develop</div><ul class="space-y-1">${gapList}</ul></div>` : ""}
      ${dbNotice}
    </div>
  `
  return createCard("Skills Gap Analysis", bodyHtml)
}

/**
 * Market research card showing demand, trends, and growth drivers.
 */
export function renderMarketResearchCard(data) {
  const demandBadge = data.demand_level
    ? `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${demandBadgeColor(data.demand_level)}">${escapeHtml(data.demand_level)}</span>`
    : ""

  const shortageBadge = data.shortage
    ? `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Shortage</span>`
    : ""

  const outlookText = data.job_outlook
    ? `<div class="flex items-center justify-between text-sm"><span class="text-gray-600">Outlook</span><span class="font-medium">${escapeHtml(data.job_outlook)}</span></div>`
    : ""

  const remoteText = data.remote_viability
    ? `<div class="flex items-center justify-between text-sm"><span class="text-gray-600">Remote viability</span><span class="font-medium">${escapeHtml(data.remote_viability)}</span></div>`
    : ""

  const drivers = (data.growth_drivers || []).map(d =>
    `<span class="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">${escapeHtml(d)}</span>`
  ).join("")

  const salarySection = data.has_salary_data && data.salary_data && data.salary_data.length > 0
    ? `<div class="divide-y divide-gray-100">${data.salary_data.map(s =>
        `<div class="flex items-center justify-between py-1.5 text-sm">
          <span class="text-gray-600">${escapeHtml(s.level || "")}</span>
          <span class="font-medium">${formatCurrency(s.median)}</span>
        </div>`
      ).join("")}</div>`
    : ""

  const bodyHtml = `
    <div class="space-y-3">
      <div class="flex items-center justify-between text-sm">
        <span class="font-medium">${escapeHtml(data.role_name || "")}</span>
        ${data.location_name ? `<span class="text-gray-500">${escapeHtml(data.location_name)}</span>` : ""}
      </div>
      ${demandBadge || shortageBadge ? `<div class="flex items-center gap-2">${demandBadge}${shortageBadge}</div>` : ""}
      ${outlookText}
      ${remoteText}
      ${salarySection}
      ${drivers ? `<div><div class="text-xs font-medium text-gray-600 mb-1">Growth drivers</div><div class="flex flex-wrap gap-1">${drivers}</div></div>` : ""}
      ${data.notes ? `<div class="text-xs text-gray-500 italic">${escapeHtml(data.notes)}</div>` : ""}
    </div>
  `
  return createCard("Market Research", bodyHtml)
}

/**
 * CV Builder suggestion card with CTA link.
 */
export function renderCvBuilderCard(data) {
  const path = escapeHtml(data.cv_builder_path || "/dashboard/cv-builder/new")
  const scoreHtml = data.cv_analysis_score != null
    ? `<div class="flex items-center gap-2 text-sm"><span class="text-gray-600">CV score</span><span class="font-semibold ${data.cv_analysis_score >= 70 ? "text-green-600" : "text-amber-600"}">${data.cv_analysis_score}/100</span></div>`
    : ""

  const existingHtml = data.has_existing_cv
    ? `<div class="text-xs text-gray-500">You have ${data.existing_cv_count} existing CV${data.existing_cv_count === 1 ? "" : "s"}</div>`
    : ""

  const bodyHtml = `
    <div class="space-y-3">
      ${data.reason ? `<p class="text-sm text-gray-700">${escapeHtml(data.reason)}</p>` : ""}
      ${scoreHtml}
      ${existingHtml}
      <a href="${path}" target="_blank" rel="noopener"
         class="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-cyan-700 text-white text-sm font-semibold hover:bg-cyan-800 transition-colors no-underline">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
        Build My CV
      </a>
    </div>
  `
  return createCard("CV Builder", bodyHtml)
}

// --- Private helpers ---

function demandBadgeColor(level) {
  switch ((level || "").toLowerCase()) {
    case "high":
    case "very_high":
      return "bg-green-100 text-green-700"
    case "medium":
      return "bg-amber-100 text-amber-700"
    case "low":
      return "bg-red-100 text-red-700"
    default:
      return "bg-gray-100 text-gray-700"
  }
}

function createCard(title, bodyHtml) {
  const card = document.createElement("div")
  card.className = "my-3 max-w-[85%] rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"

  card.innerHTML = `
    <div class="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
      <h4 class="text-xs font-semibold text-gray-700 uppercase tracking-wide">${escapeHtml(title)}</h4>
    </div>
    <div class="px-4 py-3">${bodyHtml}</div>
  `
  return card
}

function renderErrorCard(message) {
  return createCard("Error", `<p class="text-sm text-red-600">${escapeHtml(message)}</p>`)
}

function createPercentileGauge(percentile) {
  const color = percentile >= 75 ? "bg-green-500" : percentile >= 50 ? "bg-cyan-500" : percentile >= 25 ? "bg-amber-500" : "bg-red-500"
  return `
    <div class="space-y-1">
      <div class="flex items-center justify-between text-xs text-gray-500">
        <span>Percentile</span>
        <span class="font-medium text-gray-900">${percentile}th</span>
      </div>
      <div class="relative h-2 bg-gray-200 rounded-full overflow-hidden">
        <div class="${color} h-full rounded-full transition-all" style="width: ${percentile}%"></div>
      </div>
      <div class="flex justify-between text-[10px] text-gray-400">
        <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
      </div>
    </div>
  `
}

function renderNextLevel(nextLevel) {
  if (!nextLevel || !nextLevel.name) return ""
  return `
    <div class="mt-2 p-2.5 bg-cyan-50 rounded-lg text-sm">
      <div class="font-medium text-cyan-800">Next level: ${escapeHtml(nextLevel.name)}</div>
      ${nextLevel.median ? `<div class="text-cyan-600 text-xs mt-0.5">Median: ${formatCurrency(nextLevel.median)}</div>` : ""}
    </div>
  `
}

function comparisonRow(label, valueA, valueB) {
  return `
    <tr>
      <td class="py-1.5 text-gray-600">${label}</td>
      <td class="py-1.5 text-right font-medium">${valueA != null ? formatCurrency(valueA) : "-"}</td>
      <td class="py-1.5 text-right font-medium">${valueB != null ? formatCurrency(valueB) : "-"}</td>
    </tr>
  `
}

function formatCurrency(value) {
  if (value == null) return "-"
  const num = typeof value === "string" ? parseFloat(value) : value
  if (isNaN(num)) return "-"
  const abs = Math.abs(num)
  const formatted = abs >= 1000
    ? "$" + abs.toLocaleString("en-NZ", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : "$" + abs.toFixed(2)
  return num < 0 ? "-" + formatted : formatted
}

function escapeHtml(text) {
  if (!text) return ""
  const div = document.createElement("div")
  div.textContent = String(text)
  return div.innerHTML
}
